import React, { useState } from 'react'
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Tooltip,
  Badge,
  Toolbar,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Dashboard,
  Home,
  BookOnline,
  Flight,
  CalendarMonth,
  AttachMoney,
  Analytics,
  Chat as ChatIcon,
  QuestionAnswer as InquiriesIcon,
} from '@mui/icons-material'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useInquiryNotifications } from '@/hooks/useInquiryNotifications'

interface CollapsibleSidebarProps {
  open: boolean
  onToggle: () => void
}

const DRAWER_WIDTH = 240
const MINI_RAIL_WIDTH = 64

export const CollapsibleSidebar: React.FC<CollapsibleSidebarProps> = ({
  open,
  onToggle,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'))
  const location = useLocation()
  const { userProfile, isHost } = useAuth()
  const { notifications, markAsSeen } = useInquiryNotifications()
  const [railHovered, setRailHovered] = useState(false)

  const allNavigationItems = [
    { label: 'Dashboard', href: '/dashboard', icon: <Dashboard />, description: 'Overview and metrics', requiresHost: false },
    { label: 'Listings', href: '/listings', icon: <Home />, description: 'Manage your properties', requiresHost: true },
    { label: 'My Bookings', href: '/my-bookings', icon: <BookOnline />, description: 'Manage property bookings', requiresHost: true },
    { label: 'Inquiries', href: '/inquiries', icon: <InquiriesIcon />, description: 'Manage booking inquiries', requiresHost: true },
    { label: 'My Trips', href: '/my-trips', icon: <Flight />, description: 'View your travel bookings', requiresHost: false },
    { label: 'Calendar', href: '/calendar', icon: <CalendarMonth />, description: 'Calendar view of bookings', requiresHost: true },
    { label: 'Chat', href: '/chat', icon: <ChatIcon />, description: 'Real-time messaging and support', requiresHost: false },
    { label: 'Expenses', href: '/expenses', icon: <AttachMoney />, description: 'Track property expenses', requiresHost: true },
    { label: 'Financial', href: '/financial', icon: <Analytics />, description: 'Financial dashboard and reports', requiresHost: true },
  ]

  const navigationItems = allNavigationItems.filter(item => !item.requiresHost || isHost())

  const isActive = (href: string): boolean => location.pathname === href

  const getBadgeCount = (href: string): number => {
    if (href === '/inquiries' && notifications.pendingCount > 0) {
      return notifications.pendingCount
    }
    return 0
  }

  const handleNavigationClick = (href: string): void => {
    if (href === '/inquiries') {
      markAsSeen()
    }
    // Close drawer on mobile after navigation
    if (isMobile) {
      onToggle()
    }
  }

  // Determine if labels should be shown based on tier
  const showLabels = isMobile ? true : isTablet ? railHovered : true

  // Current effective width for tablet rail
  const tabletWidth = railHovered ? DRAWER_WIDTH : MINI_RAIL_WIDTH

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar spacer to push content below the fixed AppBar */}
      <Toolbar />

      {/* User Profile Section */}
      {userProfile && (
        <Box
          sx={{
            p: showLabels ? 2 : 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: showLabels ? 'flex-start' : 'center',
          }}
        >
          <Avatar
            src={userProfile.photoUrl}
            sx={{
              width: showLabels ? 40 : 32,
              height: showLabels ? 40 : 32,
              mr: showLabels ? 1.5 : 0,
            }}
          >
            {userProfile.name?.charAt(0) || userProfile.email?.charAt(0)}
          </Avatar>
          {showLabels && (
            <Box sx={{ overflow: 'hidden' }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {userProfile.name || 'User'}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
              >
                {userProfile.userGroup}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Navigation Items */}
      <Box sx={{ flexGrow: 1, py: 1 }}>
        <List sx={{ px: 1 }}>
          {navigationItems.map((item) => {
            const active = isActive(item.href)
            const badgeCount = getBadgeCount(item.href)

            const listItem = (
              <ListItem key={item.label} disablePadding>
                <ListItemButton
                  component={Link}
                  to={item.href}
                  onClick={() => handleNavigationClick(item.href)}
                  sx={{
                    minHeight: 48,
                    justifyContent: showLabels ? 'initial' : 'center',
                    px: 2.5,
                    borderRadius: 1,
                    mb: 0.5,
                    borderLeft: active ? '4px solid #f59842' : '4px solid transparent',
                    backgroundColor: active ? 'rgba(245,152,66,0.08)' : 'transparent',
                    color: active ? 'primary.main' : 'inherit',
                    '&:hover': {
                      backgroundColor: 'rgba(245,152,66,0.08)',
                    },
                    transition: 'background 150ms ease',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: showLabels ? 3 : 'auto',
                      justifyContent: 'center',
                      color: active ? 'primary.main' : 'inherit',
                    }}
                  >
                    <Badge
                      badgeContent={badgeCount}
                      color="error"
                      invisible={badgeCount === 0}
                      sx={{ '& .MuiBadge-badge': { right: -3, top: 3 } }}
                    >
                      {item.icon}
                    </Badge>
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    sx={{
                      opacity: showLabels ? 1 : 0,
                      transition: 'opacity 200ms ease',
                    }}
                  />
                  {showLabels && badgeCount > 0 && (
                    <Box
                      sx={{
                        bgcolor: 'error.main',
                        color: 'white',
                        borderRadius: '50%',
                        minWidth: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        ml: 1,
                      }}
                    >
                      {badgeCount}
                    </Box>
                  )}
                </ListItemButton>
              </ListItem>
            )

            // Wrap with tooltip when labels are hidden (collapsed tablet rail)
            if (!showLabels) {
              return (
                <Tooltip key={item.label} title={item.label} placement="right" arrow>
                  {listItem}
                </Tooltip>
              )
            }

            return listItem
          })}
        </List>
      </Box>
    </Box>
  )

  // Mobile (xs–sm): full-width temporary drawer
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: '100%',
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    )
  }

  // Tablet (sm–md): mini-rail 64px, expands on hover
  if (isTablet) {
    return (
      <Drawer
        variant="permanent"
        onMouseEnter={() => setRailHovered(true)}
        onMouseLeave={() => setRailHovered(false)}
        sx={{
          width: tabletWidth,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          '& .MuiDrawer-paper': {
            width: tabletWidth,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
            borderRight: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    )
  }

  // Desktop (md+): persistent 240px drawer always visible
  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  )
}
